import {
  ConfirmPrimitiveReservation,
  ConfirmPrimitiveReservationInput,
  MakePrimitiveReservation
} from '../../types/cart'

const isRecord = (value: unknown): value is Record<string, any> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

export function isStoredPrimitive(
  value: unknown
): value is MakePrimitiveReservation {
  return (
    isRecord(value) &&
    Number.isSafeInteger(value.reservationNumber) &&
    value.reservationNumber > 0 &&
    isRecord(value.paymentInfo) &&
    Number.isSafeInteger(value.paymentInfo.walletID) &&
    value.paymentInfo.walletID > 0 &&
    typeof value.propertyCode === 'string' &&
    Array.isArray(value.roomStays) &&
    value.roomStays.length > 0 &&
    value.roomStays.every(
      (stay: unknown) => isRecord(stay) && typeof stay.crsNumber === 'string'
    ) &&
    isRecord(value.billingSummary)
  )
}

export const isStoredConfirmInput = (
  value: unknown
): value is ConfirmPrimitiveReservationInput =>
  isRecord(value) &&
  Number.isSafeInteger(value.reservationNumber) &&
  isRecord(value.booker) &&
  isRecord(value.grpcInput) &&
  Array.isArray(value.confirmRooms) &&
  value.confirmRooms.every(
    (room: unknown) =>
      isRecord(room) &&
      typeof room.crsNumber === 'string' &&
      Array.isArray(room.profiles)
  )

export const isStoredConfirmation = (
  value: unknown
): value is ConfirmPrimitiveReservation =>
  isRecord(value) &&
  Number.isSafeInteger(value.reservationNumber) &&
  value.reservationNumber > 0 &&
  isRecord(value.reservationInfo) &&
  isRecord(value.billingSummary) &&
  isRecord(value.paymentInfo)

export function readCartState<T>(
  key: string,
  cartID: string,
  isValid: (value: unknown) => value is T
): T | undefined {
  const raw = localStorage.getItem(key)
  if (!raw) {
    return undefined
  }

  try {
    const stored = JSON.parse(raw)
    if (stored?.cartID === cartID && isValid(stored.data)) {
      return stored.data
    }
  } catch {
    // Invalid or obsolete booking state must never be used for confirmation.
  }
  localStorage.removeItem(key)
  return undefined
}

export function writeCartState<T>(key: string, cartID: string, data: T): void {
  localStorage.setItem(key, JSON.stringify({ cartID, data }))
}

export function paymentCartID(): string {
  try {
    const stored = JSON.parse(
      localStorage.getItem('confirmPrimitiveReservationState') || 'null'
    )
    return typeof stored?.cartID === 'string' &&
      isStoredConfirmation(stored.data)
      ? stored.cartID
      : ''
  } catch {
    return ''
  }
}
